import { useEffect, useRef, useState } from 'react';
import { callables } from './callables';

export type UrlVariant = 'thumb' | 'large' | 'view' | 'download';

interface Entry {
  url: string;
  expiresAt: number;
}

const BATCH = 60;
const cache = new Map<string, Entry>();

/**
 * Las miniaturas (válidas 24 h) también se guardan en el navegador para que al volver a entrar
 * aparezcan sin esperar al servidor. Solo es una conveniencia: si el almacenamiento no está
 * disponible, todo funciona igual.
 */
const STORE_KEY = 'bp.thumbUrls.v1';
const MAX_STORED = 3000;
const storable = (k: string) => k.includes('|thumb|') || k.includes('|large|');

function loadStored() {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (!raw) return;
    const now = Date.now();
    for (const [k, e] of Object.entries(JSON.parse(raw) as Record<string, Entry>)) {
      if (e.expiresAt > now) cache.set(k, e);
    }
  } catch {
    // Sin almacenamiento (ventana privada, bloqueado): solo caché en memoria.
  }
}

let saveTimer: ReturnType<typeof setTimeout> | null = null;
function saveStored() {
  saveTimer ??= setTimeout(() => {
    saveTimer = null;
    try {
      const now = Date.now();
      const entries = [...cache.entries()]
        .filter(([k, e]) => storable(k) && e.expiresAt > now)
        .slice(-MAX_STORED);
      localStorage.setItem(STORE_KEY, JSON.stringify(Object.fromEntries(entries)));
    } catch {
      // Lleno o bloqueado: se ignora.
    }
  }, 500);
}

loadStored();
const pending = new Map<
  string,
  { resolve: (url: string | null) => void; reject: (e: unknown) => void }[]
>();
const queues = new Map<string, Set<string>>(); // `${campaignId}|${variant}` → assetIds
let timer: ReturnType<typeof setTimeout> | null = null;

const key = (campaignId: string, variant: UrlVariant, id: string) =>
  `${campaignId}|${variant}|${id}`;

async function flush() {
  timer = null;
  const work = [...queues.entries()];
  queues.clear();
  await Promise.all(
    work.flatMap(([qk, ids]) => {
      const [campaignId, variant] = qk.split('|') as [string, UrlVariant];
      const list = [...ids];
      const chunks: string[][] = [];
      for (let i = 0; i < list.length; i += BATCH) chunks.push(list.slice(i, i + BATCH));
      return chunks.map(async (assetIds) => {
        try {
          const { data } = await callables.getAssetUrls({ campaignId, assetIds, variant });
          // Margen de 60 s para no usar una URL a punto de expirar.
          const expiresAt = Date.now() + (data.expiresInSeconds - 60) * 1000;
          for (const id of assetIds) {
            const k = key(campaignId, variant, id);
            const url = data.urls[id] ?? null;
            if (url) cache.set(k, { url, expiresAt });
            if (url && storable(k)) saveStored();
            pending.get(k)?.forEach((p) => p.resolve(url));
            pending.delete(k);
          }
        } catch (error) {
          for (const id of assetIds) {
            const k = key(campaignId, variant, id);
            pending.get(k)?.forEach((p) => p.reject(error));
            pending.delete(k);
          }
        }
      });
    }),
  );
}

/**
 * URL temporal de una pieza. Las solicitudes se agrupan en lotes (una llamada por hasta 60
 * piezas) y se guardan en caché hasta poco antes de expirar.
 */
export function getAssetUrl(
  campaignId: string,
  id: string,
  variant: UrlVariant,
): Promise<string | null> {
  const k = key(campaignId, variant, id);
  const hit = cache.get(k);
  if (hit && hit.expiresAt > Date.now()) return Promise.resolve(hit.url);
  return new Promise((resolve, reject) => {
    const waiting = pending.get(k);
    pending.set(k, [...(waiting ?? []), { resolve, reject }]);
    if (waiting) return; // ya está en camino
    const qk = `${campaignId}|${variant}`;
    queues.set(qk, (queues.get(qk) ?? new Set()).add(id));
    timer ??= setTimeout(() => void flush(), 30);
  });
}

/** Pide la URL solo cuando el elemento entra (o está por entrar) en pantalla. */
export function useLazyAssetUrl(campaignId: string, id: string | null, variant: UrlVariant) {
  const ref = useRef<HTMLElement | null>(null);
  const [visible, setVisible] = useState(false);
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || visible) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          io.disconnect();
        }
      },
      { rootMargin: '400px 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [visible]);

  useEffect(() => {
    // Sin pieza (p. ej. campaña sin portada) no se pide nada.
    if (!visible || !id) return;
    let alive = true;
    getAssetUrl(campaignId, id, variant).then(
      (u) => alive && (u ? setUrl(u) : setFailed(true)),
      () => alive && setFailed(true),
    );
    return () => {
      alive = false;
    };
  }, [visible, campaignId, id, variant]);

  return { ref, url, failed };
}
